import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { BookOpen } from "lucide-react";
import { LOGO_URL, CLUB } from "@/data";

const SPRITES = [
    {
        id: "dragon",
        src: "/cards/sprites/dragon.png",
        alt: "Draghetto viola",
        w: 96,
        pos: { top: "7%", left: "1%" },
        dur: 5.4,
        delayIn: 0.15,
        fly: { x: -300, y: -220, rotate: -70 },
        delayOut: 0.1,
    },
    {
        id: "rocket",
        src: "/cards/sprites/rocket.png",
        alt: "Razzo",
        w: 88,
        pos: { top: "2%", right: "9%" },
        dur: 6.2,
        delayIn: 0.3,
        fly: { x: 300, y: -260, rotate: 50 },
        delayOut: 0.2,
    },
    {
        id: "knight",
        src: "/cards/sprites/knight.png",
        alt: "Cavaliere su unicorno",
        w: 106,
        pos: { bottom: "17%", right: "-2%" },
        dur: 5.8,
        delayIn: 0.45,
        fly: { x: 340, y: 130, rotate: -18 },
        delayOut: 0.3,
    },
];

const RIVETS = [
    { top: 18, left: 30 }, { top: 30, left: 18 },
    { top: 18, right: 30 }, { top: 30, right: 18 },
    { bottom: 18, left: 30 }, { bottom: 30, left: 18 },
    { bottom: 18, right: 30 }, { bottom: 30, right: 18 },
];

const DUST = Array.from({ length: 20 }, (_, i) => ({
    left: (i * 53 + 11) % 100,
    top: (i * 37 + 7) % 100,
    delay: (i % 7) * 0.9,
    dur: 8 + (i % 5) * 3,
    size: 2 + (i % 3) * 2,
}));

export default function Cover({ onOpenStart, onFinished }) {
    const [opening, setOpening] = useState(false);
    const [tilt, setTilt] = useState({ x: 0, y: 0 });

    const open = () => {
        if (opening) return;
        setOpening(true);
        onOpenStart();
        setTimeout(onFinished, 2100);
    };

    const onMove = (e) => {
        if (opening) return;
        const r = e.currentTarget.getBoundingClientRect();
        setTilt({
            x: ((e.clientY - r.top) / r.height - 0.5) * -9,
            y: ((e.clientX - r.left) / r.width - 0.5) * 12,
        });
    };

    return (
        <motion.div
            className="cover-root desk-bg"
            exit={{ opacity: 0, transition: { duration: 0.7 } }}
            data-testid="cover-screen"
        >
            {DUST.map((d, i) => (
                <span
                    key={i}
                    className="dust"
                    style={{
                        left: `${d.left}%`,
                        top: `${d.top}%`,
                        animationDelay: `${d.delay}s`,
                        animationDuration: `${d.dur}s`,
                        width: d.size,
                        height: d.size,
                    }}
                />
            ))}

            <div
                className="relative z-[1] h-full w-full flex flex-col items-center justify-center"
                style={{ perspective: "2200px" }}
                onMouseMove={onMove}
                onMouseLeave={() => !opening && setTilt({ x: 0, y: 0 })}
            >
                <motion.div
                    className="relative"
                    style={{
                        width: "min(400px, 82vw)",
                        aspectRatio: "3/4",
                        transformStyle: "preserve-3d",
                        transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
                        transition: "transform 0.25s ease-out",
                    }}
                    initial={{ y: 36, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                >
                    {/* page block revealed under the cover */}
                    <div className="pages-block">
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-10">
                            <p className="f-title text-[0.62rem] tracking-[0.4em] uppercase text-[#8a683c]">Il racconto comincia</p>
                            <p className="f-body italic text-[#6d5638] mt-3 text-lg">Qui la storia prende il suo primo respiro…</p>
                        </div>
                    </div>

                    {/* characters, ready to scatter when the tome opens */}
                    {SPRITES.map((s) => (
                        <div
                            key={s.id}
                            className="absolute z-[2] pointer-events-none select-none"
                            style={{
                                width: `min(${s.w}px, 17vw)`,
                                ...s.pos,
                                animation: `floaty ${s.dur}s ease-in-out ${s.delayIn}s infinite`,
                            }}
                        >
                            <motion.img
                                src={s.src}
                                alt={s.alt}
                                draggable={false}
                                className="block w-full h-auto"
                                initial={{ opacity: 0, scale: 0.6, x: 0, y: 0, rotate: 0 }}
                                animate={
                                    opening
                                        ? { x: s.fly.x, y: s.fly.y, rotate: s.fly.rotate, opacity: 0, scale: 1.05 }
                                        : { opacity: 1, scale: 1, x: 0, y: 0, rotate: 0 }
                                }
                                transition={
                                    opening
                                        ? { duration: 0.9, delay: s.delayOut, ease: [0.5, 0, 0.85, 0.4] }
                                        : { duration: 0.8, delay: 0.9 + s.delayIn, ease: [0.22, 1, 0.36, 1] }
                                }
                            />
                        </div>
                    ))}

                    {/* front cover */}
                    <motion.div
                        className="tome-cover cursor-pointer"
                        data-testid="cover-open-button"
                        role="button"
                        aria-label="Apri il tomo"
                        onClick={open}
                        animate={opening ? { rotateY: -168 } : { rotateY: 0 }}
                        transition={{ duration: 1.7, ease: [0.6, 0.05, 0.3, 1] }}
                        whileHover={opening ? undefined : { scale: 1.015 }}
                        whileTap={opening ? undefined : { scale: 0.985 }}
                        style={{ transformOrigin: "left center" }}
                    >
                        <div className="spine" />
                        <div className="corner corner-tl" />
                        <div className="corner corner-tr" />
                        <div className="corner corner-bl" />
                        <div className="corner corner-br" />
                        {RIVETS.map((p, i) => (
                            <span key={i} className="rivet" style={p} />
                        ))}
                        <div className="band band-top" />
                        <div className="band band-bottom" />
                        <div className="buckle" />
                    </motion.div>
                </motion.div>

                <motion.div
                    className="mt-10 flex flex-col items-center gap-2"
                    animate={{ opacity: opening ? 0 : 1, transition: { duration: 0.4 } }}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                >
                    <p
                        className="hint-pulse f-title text-[0.58rem] tracking-[0.2em] sm:text-[0.68rem] sm:tracking-[0.35em] uppercase flex items-center gap-3 text-center px-4"
                        style={{ color: "#f2e4c2", textShadow: "0 1px 10px rgba(20,10,4,0.9), 0 0 2px rgba(20,10,4,0.9)" }}
                    >
                        <BookOpen size={15} className="shrink-0" /> Tocca il tomo per aprire il racconto
                    </p>
                </motion.div>
            </div>
        </motion.div>
    );
}
